import axios from 'axios';
import type { MassiveCameraRow } from '../utils/parseMassiveCamerasExcel';

export function buildRecordingServerUrl(recordingsIp: string): string {
  const raw = recordingsIp.trim().replace(/\/$/, '');
  if (/^https?:\/\//i.test(raw)) return raw;
  // Playback / video-engine suele usar :9090; el API /api/camera/* del appliance es puerto 80.
  return `http://${raw}:9090`;
}

export function buildRecordingsHostForAppliance(recordingsIp: string): string {
  const raw = recordingsIp.trim().replace(/\/$/, '');
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      return u.host.includes(':') ? u.hostname : u.host;
    } catch {
      return raw.replace(/^https?:\/\//i, '').split('/')[0].split(':')[0];
    }
  }
  return raw.split(':')[0];
}

/**
 * Crea XTAM Server + cámaras locales desde Excel y sincroniza POST /api/camera/bulk en el appliance.
 */
export async function provisionXtamMasivosSite(opts: {
  apiUrl: string;
  authHeaders: Record<string, string>;
  siteId: number;
  siteName: string;
  siteAddress: string;
  streamingIp: string;
  recordingsIp: string;
  cameras: MassiveCameraRow[];
  latitude?: string;
  longitude?: string;
}): Promise<string[]> {
  const errors: string[] = [];
  const {
    apiUrl,
    authHeaders,
    siteId,
    siteName,
    siteAddress,
    streamingIp,
    recordingsIp,
    cameras,
  } = opts;

  if (!cameras.length) {
    errors.push('El Excel no tiene cámaras RTSP válidas');
    return errors;
  }

  const recordingServerUrl = buildRecordingServerUrl(recordingsIp);
  const recordingsHost = buildRecordingsHostForAppliance(recordingsIp);
  const serverSerial = `XTAM-SERVER-${streamingIp.replace(/\./g, '-') || 'SITE'}`.slice(0, 45);
  const macFromIp = (() => {
    const octets = streamingIp.split('.').map((p) => Math.max(0, Math.min(255, parseInt(p, 10) || 0)));
    while (octets.length < 4) octets.push(0);
    const hex = octets.map((n) => n.toString(16).padStart(2, '0'));
    return `02:00:${hex.join(':')}`;
  })();

  // 1) XTAM Server en inventario + vínculo al sitio
  try {
    let inventoryXtamId: number | null = null;
    const listRes = await axios.get(`${apiUrl}/inventory/xtam-videos`, { headers: authHeaders }).catch(() => null);
    const existingList = listRes?.data?.data || listRes?.data || [];
    const found = (Array.isArray(existingList) ? existingList : []).find(
      (x: any) =>
        String(x.serialDevice || x.serial_device || '').toUpperCase() === serverSerial.toUpperCase()
    );
    if (found?.id) {
      inventoryXtamId = found.id;
    } else {
      let xtamModelId: number | null = null;
      try {
        const modelsRes = await axios.get(`${apiUrl}/inventory/xtam-videos/models`, { headers: authHeaders });
        const models = modelsRes?.data?.data || modelsRes?.data || [];
        if (Array.isArray(models) && models.length > 0) xtamModelId = models[0].id;
      } catch (_) { /* opcional */ }

      const createInv = await axios.post(
        `${apiUrl}/inventory/xtam-videos`,
        {
          serialDevice: serverSerial,
          mac: macFromIp,
          ...(xtamModelId != null ? { idModel: xtamModelId } : {}),
        },
        { headers: authHeaders }
      );
      inventoryXtamId = createInv.data?.data?.id ?? createInv.data?.id ?? null;
    }

    if (!inventoryXtamId) {
      errors.push('No se pudo crear/obtener el XTAM Server en inventario');
    } else {
      await axios.post(
        `${apiUrl}/sites/${siteId}/xtam-videos`,
        {
          inventoryXtamVideosId: inventoryXtamId,
          ipAddress: streamingIp,
          state: null,
        },
        { headers: authHeaders }
      );
    }
  } catch (err: any) {
    const msg =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message ||
      'Error desconocido';
    errors.push(`XTAM Server: ${msg}`);
  }

  // 2) Marca/modelo cámaras
  let cameraBrandId = 1;
  let cameraModelId = 1;
  try {
    const [brandsRes, modelsRes] = await Promise.all([
      axios.get(`${apiUrl}/inventory/cameras/brands`, { headers: authHeaders }).catch(() => null),
      axios.get(`${apiUrl}/inventory/cameras/models`, { headers: authHeaders }).catch(() => null),
    ]);
    const brands = brandsRes?.data?.data || [];
    const models = modelsRes?.data?.data || [];
    if (brands.length > 0) cameraBrandId = brands[0].id;
    if (models.length > 0) cameraModelId = models[0].id;
  } catch (_) { /* defaults */ }

  const baseLat = opts.latitude ? parseFloat(opts.latitude) : null;
  const baseLng = opts.longitude ? parseFloat(opts.longitude) : null;
  const offsetDeg = 0.0001;

  // 3) Cámaras locales VMS
  for (let i = 0; i < cameras.length; i++) {
    const cam = cameras[i];
    const channel = i + 1;
    try {
      const invResponse = await axios.post(
        `${apiUrl}/inventory/cameras/find-or-create`,
        {
          serial: `${serverSerial}-camara${channel}`.slice(0, 45),
          plate: 'N/A',
          mac: null,
          idBrand: cameraBrandId,
          idModel: cameraModelId,
          isActive: true,
        },
        { headers: authHeaders }
      );
      const inventoryCameraId = invResponse.data?.data?.id ?? invResponse.data?.id;
      if (!inventoryCameraId) {
        errors.push(`Cámara ${channel}: sin ID de inventario`);
        continue;
      }
      const latOffset = baseLat != null ? (baseLat + i * offsetDeg).toFixed(6) : undefined;
      const lngOffset = baseLng != null ? (baseLng + i * offsetDeg).toFixed(6) : undefined;
      await axios.post(
        `${apiUrl}/sites/${siteId}/cameras`,
        {
          inventoryCamerasId: inventoryCameraId,
          name: cam.name || `${siteName} Camara ${channel}`,
          siteAddress,
          ipAddress: streamingIp,
          urlRtsp: `rtsp://${streamingIp}:8554/camara${channel}`,
          urlRtspOriginal: cam.rtsp,
          latitude: latOffset,
          longitude: lngOffset,
          xtamVideoChannel: `camara${channel}`,
          isFromDvr: false,
          isAllowAnalytics: true,
          isAllowTesting: true,
          vmsId: '0',
          isOnvif: false,
          isMassive: true,
          recordingServerUrl,
        },
        { headers: authHeaders }
      );
    } catch (err: any) {
      const msg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Error desconocido';
      errors.push(`Cámara ${channel}: ${msg}`);
    }
  }

  // 4) Sync appliance POST /api/camera/bulk (credenciales en env del backend)
  try {
    await axios.post(
      `${apiUrl}/sites/${siteId}/massive-cameras/bulk`,
      {
        recordingsHost,
        skipDuplicates: true,
        recordingFileType: 'mkv',
        cameras: cameras.map((c) =>
          c.recordingFileType
            ? { rtsp: c.rtsp, recording_file_type: c.recordingFileType }
            : c.rtsp
        ),
      },
      { headers: authHeaders }
    );
  } catch (err: any) {
    const msg =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message ||
      'Error desconocido';
    errors.push(`Bulk appliance: ${msg}`);
  }

  return errors;
}
